import {
  IPartNote,
  IPartNoteForm,
  mapCreatePartNotes,
  mapNotesToManagedNotes,
} from 'bom/components/AddPartNote';
import { usePartNotes } from 'bom/hooks';
import { useFormContext, useNotificationContext } from 'core/states';
import { INote } from 'core/types';
import { usePartBomItemNoteActions } from 'partBoms/hooks';
import { IPartBomItemNote } from 'partBoms/types';

interface IProps {
  notes: IPartBomItemNote[];
  setNotes: (value: IPartBomItemNote[]) => void;
  isEdit?: boolean;
}

export const useNotes = ({ notes = [], setNotes, isEdit = false }: IProps) => {
  const { clearNotification } = useNotificationContext();
  const { getValues, setValue } = useFormContext();

  const { removePartBomItemNotes, upsertPartBomItemNote } = usePartBomItemNoteActions();

  const onAddPartNote = async (data: IPartNoteForm) => {
    const { success, partBomItemNote } = await upsertPartBomItemNote({
      partBomItemNote: mapCreatePartNotes(data),
    });

    if (success) {
      setTimeout(() => {
        handleOnSubmitSuccess(partBomItemNote);
        clearNotification();
      }, 1000);
    }
  };

  const onEditPartNote = async (data: Partial<IPartNote>) => {
    const { success, partBomItemNote } = await upsertPartBomItemNote({
      partBomItemNote: {
        id: data.id,
        content: data.content,
        type: data.type,
      },
    });

    if (success) {
      setTimeout(() => {
        handleOnSubmitSuccess(partBomItemNote);
        clearNotification();
      }, 1000);
    }

    return Promise.resolve({ success });
  };

  const onRemovePartNote = async (id: number) => {
    const { success } = await removePartBomItemNotes({
      ids: [id],
    });

    if (success) {
      handleOnSubmitSuccess(undefined, id);
    }
  };

  const handleOnSubmitSuccess = (note?: IPartBomItemNote, deleteNoteId?: number) => {
    const prevNotes = getValues('notes');

    const udpdatedNotesObject: Record<number, IPartBomItemNote> = {};

    prevNotes.forEach((noteItem: IPartBomItemNote) => {
      udpdatedNotesObject[noteItem.id] = noteItem;
    });

    if (deleteNoteId) {
      delete udpdatedNotesObject[deleteNoteId];
    }

    if (note) {
      udpdatedNotesObject[note.id] = note;
    }

    setValue?.('notes', mapNotesToManagedNotes(Object.values(udpdatedNotesObject)));
  };

  const { onAddNote, onDeleteNote, onEditNote } = usePartNotes({
    notes: notes as INote[],
    setNotes: (value: INote[]) => setNotes(value as IPartBomItemNote[]),
    isEdit,
    onAddPartNote,
    onEditPartNote,
    onRemovePartNote,
  });

  return {
    onAddNote,
    onDeleteNote,
    onEditNote,
  };
};
